<template>
  <div>
    <slot name="category" />
    <div class="columns is-centered">
      <div class="column is-9">
        <main v-if="post">
          <nav v-if="post.type === 'TYPE_POST'" class="my-breadcrumb" aria-label="breadcrumbs">
            <ul>
              <li>
                <nuxt-link :to="localePath('index')">
                  {{ post.storeName.name }}
                </nuxt-link>
              </li>
              <li>
                <i class="core-icons core-icons-chevron-right" /> <nuxt-link :to="localePath('cms-blog')">
                  {{ $t('fields.blog') }}
                </nuxt-link>
              </li>
              <li><i class="core-icons core-icons-chevron-right" />{{ post.description.name }}</li>
            </ul>
          </nav>

          <div class="content">
            <page-title>{{ post.description.name }}</page-title>
            <article>
              <full-description :description="post.productDescriptionSections" />
            </article>
            <b-taglist v-if="post.type === 'TYPE_POST'" class="post-tags">
              <b-tag class="author">
                {{ post.creator }} • {{ $moment(post.dateOfPublication ).format('LL') }}
              </b-tag>
              <template v-if="post.tags">
                <b-tag v-for="(tag, index) in post.tags.split(',')" :key="index">
                  #{{ tag }}
                </b-tag>
              </template>
            </b-taglist>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import FullDescription from '../../components/shared/FullDescription'
export default {
  name: 'CorePostPage',
  components: { FullDescription },
  layout: 'page',
  props: {
    post: {
      type: Object,
      default: () => { }
    },
    slugs: {
      type: Array,
      default: () => []
    }
  },
  head () {
    const i18nHead = this.$nuxtI18nHead({ addSeoAttributes: true })

    const mainLang = process.env.lang
    const actualLanguage = this._locale

    const links = this._clone(i18nHead.link)
    links.forEach((element) => {
      let lang = null
      if (element.hid === 'i18n-alt-' + element.hreflang) {
        lang = element.hreflang.toLowerCase()
      }
      if (element.hid === 'i18n-xd') {
        lang = mainLang
      }
      if (element.hid === 'i18n-can') {
        lang = actualLanguage
      }

      if (lang) {
        const find = this.slugs.find(x => x.lang === lang)
        if (find && find.url) {
          element.href = this._frontBaseUrl + this.localePath({ name: 'cms-postPage', params: { slug: find.url } }, lang)
        } else if (this.post.type === 'TYPE_POST') {
          element.href = this._frontBaseUrl + this.localePath('cms-blog', lang)
        } else {
          element.href = this._frontBaseUrl + this.localePath('index', lang)
        }
      }
    })

    return {
      titleTemplate: '%s',
      title: this.post.description.nameSeo || this.post.description.name,
      meta: [
        { hid: 'description', name: 'description', content: this.post.description.descriptionSeo || this.post.description.description },
        { property: 'og:site_name', content: this.post.storeName ? this.post.storeName.name : '' },
        { property: 'og:type', content: 'website' },
        { property: 'og:title', content: this.post.description.nameSeo || this.post.description.name },
        { property: 'og:description', content: this.post.description.descriptionSeo || this.post.description.description },
        { property: 'og:url', content: this._frontBaseUrl + this.$route.fullPath },
        { name: 'twitter:card', content: 'summary' },
        { name: 'twitter:title', content: this.post.description.nameSeo || this.post.description.name },
        { name: 'twitter:description', content: this.post.description.descriptionSeo || this.post.description.description },
        { name: 'twitter:url', content: this._frontBaseUrl + this.$route.fullPath },
        { name: 'twitter:label1', content: 'Written by' },
        { name: 'twitter:data1', content: this.post.creator },
        { name: 'twitter:label2', content: 'Filed under' },
        { name: 'twitter:data2', content: this.post.tags || '' },
        ...((this.post.twitter_image || this.post.mainImage) ? [{ name: 'twitter:image', content: this.post.mainImage.sizes.w2000 }] : []),
        ...((this.post.og_image || this.post.mainImage) ? [{ property: 'og:image', content: this.post.mainImage.sizes.w2000 }] : []),
        ...(this.post.noIndex ? [{ name: 'robots', content: 'noindex,nofollow' }] : []),
        ...i18nHead.meta
      ],
      script: [
        { type: 'application/ld+json', json: this.getSchemaOrg },
        { type: 'application/ld+json', json: this.getBreadcrumbSchema }
      ],
      htmlAttrs: {
        ...i18nHead.htmlAttrs
      },
      link: [
        ...this.metaLink(links)
      ]
    }
  },
  computed: {
    getBreadcrumbSchema () {
      const schema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: this.post.storeName.name,
            item: this._frontBaseUrl + this.localePath('index')
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: this.$t('fields.blog'),
            item: this._frontBaseUrl + this.localePath('cms-blog')
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: this.post.description.name,
            item: this._frontBaseUrl + this.localePath({ name: 'cms-postPage', params: { slug: this.post.url } })
          }
        ]
      }
      return schema
    },
    getSchemaOrg () {
      const schema = {
        '@context': 'https://schema.org/',
        '@type': 'Article',
        publisher: {
          '@type': 'Organization',
          // name: this.settings.title,
          url: this._frontBaseUrl
          // logo: {
          //   '@type': 'ImageObject',
          //   url: this.settings.logo
          // }
        },
        author: {
          '@type': 'Person',
          name: this.post.creator
          // image: {
          //   '@type': 'ImageObject',
          //   url: this.post.primary_author.profile_image,
          //   width: 250,
          //   height: 250
          // },
          // sameAs: []
        },
        headline: this.post.description.nameSeo || this.post.description.name,
        url: this._frontBaseUrl + this.$route.fullPath,
        keywords: this.post.tags || '',
        description: this.post.description.descriptionSeo || this.post.description.description,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': this._frontBaseUrl
        }

      }
      return schema
    }
  },
  watch: {
    _locale (lang) {
      const find = this.slugs.find(x => x.lang === lang)
      if (find && find.url) {
        this.$router.push(this.localePath({ name: 'cms-postPage', params: { slug: find.url } }))
      } else if (this.post.type === 'TYPE_POST') {
        this.$router.push(this.localePath('cms-blog'))
      } else {
        this.$router.push(this.localePath('index'))
      }
    }
  }
}
</script>
